import Component from 'flarum/common/Component';
import app from 'flarum/forum/app';
import supportedLangs from './supportedLangs';

/*
 * The composer's per-post language control, plus the mismatch prompt.
 *
 * The prompt only ever ASKS. It never changes the selection and never blocks
 * the post: the author is the authority on what language they wrote in, and a
 * detector that overrode them would be wrong in exactly the cases that matter
 * (code-switching, quotes, short posts).
 *
 * Detection runs on the server so there is one implementation of it. This
 * debounces and only asks once the text is worth judging — a detector called on
 * every keystroke is both wasteful and, on three words, meaningless.
 */
export default class SourceLangControl extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.composer = this.attrs.composer;
    this.value = (app.session.user && app.session.user.attribute('sourceLang')) || '';
    this.composer.sourceLang = this.value || null;
    this.mismatch = null;
    this.timer = null;

    // Watch the text through the composer's own content stream rather than a
    // keystroke handler: it is the value the composer will actually submit, and
    // it survives paste, undo and the markdown toolbar, which an `oninput` on
    // the textarea does not.
    const content = this.composer.fields && this.composer.fields.content;
    if (content && typeof content.map === 'function') {
      this.watcher = content.map(() => this.check());
    }
  }

  onremove() {
    clearTimeout(this.timer);
  }

  view() {
    return (
      <div className="SfComposerLang">
        <label className="SfComposerLang-label">
          {app.translator.trans('scoreflection-translate.forum.composer.written_in')}
        </label>
        <select
          className="FormControl SfComposerLang-select"
          value={this.value}
          onchange={(e) => {
            this.value = e.target.value;
            this.composer.sourceLang = this.value || null;
            this.mismatch = null;
            this.check();
          }}
        >
          <option value="">—</option>
          {supportedLangs().map((l) => (
            <option value={l}>{l.toUpperCase()}</option>
          ))}
        </select>
        {this.mismatch ? (
          <span className="SfComposerLang-warning">
            {app.translator.trans('scoreflection-translate.forum.composer.mismatch', {
              detected: this.mismatch.toUpperCase(),
            })}
          </span>
        ) : null}
      </div>
    );
  }

  /** Called by the composer extension below on input, already debounced there. */
  check() {
    clearTimeout(this.timer);
    if (!this.value) return;

    this.timer = setTimeout(() => {
      const text = this.composer.fields && this.composer.fields.content();
      if (!text || text.split(/\s+/).length < 8) {
        this.mismatch = null;
        m.redraw();
        return;
      }

      app
        .request({
          method: 'POST',
          url: `${app.forum.attribute('apiUrl')}/detect-language`,
          body: { data: { text, declared: this.value } },
        })
        .then((result) => {
          const a = result.data.attributes;
          this.mismatch = a.agrees ? null : a.detected;
          m.redraw();
        })
        .catch(() => {
          // A failed check must never obstruct posting.
          this.mismatch = null;
        });
    }, 800);
  }
}
